import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Shield, ArrowLeft, Play, Clock, Users, Zap } from 'lucide-react';
import VideoModal from '../../components/VideoModal';

const DemoPage = () => {
  const navigate = useNavigate();
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);

  const demoVideos = [
    {
      id: 1,
      title: 'Platform Overview',
      duration: '5:23',
      description: 'Get a complete overview of the RMM Pro platform and its key features',
      thumbnail: 'platform-overview',
      videoUrl: '', // Add YouTube/Vimeo embed URL here
      category: 'Getting Started'
    },
    {
      id: 2,
      title: 'Asset Management',
      duration: '8:45',
      description: 'Learn how to monitor and manage all your endpoints from a single dashboard',
      thumbnail: 'asset-management',
      videoUrl: '',
      category: 'Core Features'
    },
    {
      id: 3,
      title: 'Automated Monitoring',
      duration: '6:12',
      description: 'Set up automated monitoring, alerts, and health checks for your fleet',
      thumbnail: 'monitoring',
      videoUrl: '',
      category: 'Core Features'
    },
    {
      id: 4,
      title: 'Help Desk & Ticketing',
      duration: '7:30',
      description: 'Manage tickets, create workflows, and provide excellent customer support',
      thumbnail: 'helpdesk',
      videoUrl: '',
      category: 'Core Features'
    },
    {
      id: 5,
      title: 'PSA & Billing',
      duration: '9:15',
      description: 'Track time, manage projects, and automate your billing process',
      thumbnail: 'psa',
      videoUrl: '',
      category: 'Business Tools'
    },
    {
      id: 6,
      title: 'Automation & Scripts',
      duration: '10:20',
      description: 'Create powerful automation workflows and deploy custom scripts',
      thumbnail: 'automation',
      videoUrl: '',
      category: 'Advanced'
    }
  ];

  const handlePlayVideo = (video) => {
    setSelectedVideo(video);
    setShowVideoModal(true);
  };

  const getThumbnailGradient = (thumbnail) => {
    const gradients = {
      'platform-overview': 'from-blue-600 to-purple-600',
      'asset-management': 'from-green-600 to-teal-600',
      'monitoring': 'from-orange-600 to-red-600',
      'helpdesk': 'from-pink-600 to-purple-600',
      'psa': 'from-indigo-600 to-blue-600',
      'automation': 'from-cyan-600 to-blue-600'
    };
    return gradients[thumbnail] || 'from-slate-600 to-slate-700';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="mr-2">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800">RMM Pro</span>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={() => navigate('/login')}>
                Login
              </Button>
              <Button onClick={() => navigate('/signup')} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                Start Free Trial
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              See RMM Pro In Action
            </h1>
            <p className="text-xl text-slate-600 mb-8">
              Watch our demo videos to learn how RMM Pro can transform your MSP business
            </p>
            
            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-8 text-sm">
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-blue-600" />
                <span className="text-slate-600">6 Video Tutorials</span>
              </div>
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-purple-600" />
                <span className="text-slate-600">Watch at Your Pace</span>
              </div>
              <div className="flex items-center space-x-2">
                <Zap className="w-5 h-5 text-green-600" />
                <span className="text-slate-600">Start Trial After</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {demoVideos.map((video) => (
              <Card 
                key={video.id} 
                className="border-slate-200 hover:shadow-xl transition-all cursor-pointer group"
                onClick={() => handlePlayVideo(video)}
              >
                <div className="relative overflow-hidden">
                  {/* Thumbnail */}
                  <div className={`aspect-video bg-gradient-to-br ${getThumbnailGradient(video.thumbnail)} flex items-center justify-center relative`}>
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors"></div>
                    <div className="relative z-10 w-16 h-16 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="w-8 h-8 text-blue-600 ml-1" />
                    </div>
                    <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded">
                      {video.duration}
                    </div>
                  </div>
                </div>

                <CardHeader>
                  <div className="text-xs font-semibold text-blue-600 mb-2">{video.category}</div>
                  <CardTitle className="text-lg group-hover:text-blue-600 transition-colors">
                    {video.title}
                  </CardTitle>
                </CardHeader>

                <CardContent>
                  <p className="text-sm text-slate-600">{video.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Start your 14-day free trial and experience the power of RMM Pro
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              size="lg" 
              onClick={() => navigate('/signup')}
              className="bg-white text-blue-600 hover:bg-slate-100 text-lg px-8"
            >
              Start Free Trial
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => navigate('/public/pricing')}
              className="border-white text-white hover:bg-white/10 text-lg px-8"
            >
              View Pricing
            </Button>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      <VideoModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        videoUrl={selectedVideo?.videoUrl}
        title={selectedVideo?.title}
      />
    </div>
  );
};

export default DemoPage;